import { useEffect, useMemo, useState, type ReactNode } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

import { cn } from "@/lib/utils";

type RichTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: number;
  enableSourceToggle?: boolean;
  editorRef?: React.MutableRefObject<Editor | null>;
  variableRegistry?: Record<string, string>;
  enableVariableCommands?: boolean;
};

const FONT_OPTIONS = [
  { label: "Default", value: "inherit" },
  { label: "Sans Serif", value: "Arial" },
  { label: "Serif", value: "Georgia" },
  { label: "Monospace", value: "Courier New" },
];

const DEFAULT_COLOR = "#3b82f6";
const INLINE_TOKEN_REGEX = /\{\{\s*([a-zA-Z0-9._-]+)\s*\}\}/g;
const VARIABLE_COMMAND_REGEX = /\{\{\s*([a-zA-Z0-9._-]*)$/;

const InlineTokenHighlight = Extension.create({
  name: "inlineTokenHighlight",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("inlineTokenHighlight"),
        props: {
          decorations(state) {
            const decorations: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (!node.isText || !node.text) return;
              const text = node.text;
              let match: RegExpExecArray | null;
              INLINE_TOKEN_REGEX.lastIndex = 0;
              while ((match = INLINE_TOKEN_REGEX.exec(text)) !== null) {
                const from = pos + match.index;
                const to = from + match[0].length;
                decorations.push(
                  Decoration.inline(from, to, {
                    class: "inline-token",
                    "data-token": match[1],
                  }),
                );
              }
            });
            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  className,
  minHeight = 160,
  enableSourceToggle = false,
  editorRef,
  variableRegistry,
  enableVariableCommands = false,
}: RichTextEditorProps) {
  const [selectedFont, setSelectedFont] = useState(FONT_OPTIONS[0].value);
  const [selectedColor, setSelectedColor] = useState(DEFAULT_COLOR);
  const [mode, setMode] = useState<"visual" | "html">("visual");
  const [variableCommandOpen, setVariableCommandOpen] = useState(false);
  const [variableCommandQuery, setVariableCommandQuery] = useState("");
  const [variableCommandRange, setVariableCommandRange] = useState<{ from: number; to: number } | null>(null);
  const [activeVariableIndex, setActiveVariableIndex] = useState(0);

  const variableEntries = useMemo(() => Object.entries(variableRegistry ?? {}), [variableRegistry]);
  const filteredVariables = useMemo(() => {
    const query = variableCommandQuery.trim().toLowerCase();
    if (!query) return variableEntries;
    return variableEntries.filter(([key, description]) => {
      const haystack = `${key} ${description ?? ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [variableCommandQuery, variableEntries]);

  const extensions = useMemo(() => {
    const list = [
      StarterKit.configure({
        bulletList: { keepAttributes: true, keepMarks: true },
        orderedList: { keepAttributes: true, keepMarks: true },
      }),
      Underline,
      TextStyle,
      Color.configure({ types: ["textStyle"] }),
      FontFamily.configure({ types: ["textStyle"] }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          class: "text-primary underline decoration-dotted hover:text-primary/80",
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || "Add content…",
      }),
    ];
    if (enableVariableCommands || (variableRegistry && Object.keys(variableRegistry).length > 0)) {
      list.splice(1, 0, InlineTokenHighlight);
    }
    return list;
  }, [enableVariableCommands, placeholder, variableRegistry]);

  const editor = useEditor({
    content: value || "",
    extensions,
    editorProps: {
      attributes: {
        class:
          "w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring",
      },
      handleKeyDown: (_, event) => {
        if (!variableCommandOpen) {
          return false;
        }
        if (event.key === "Escape") {
          setVariableCommandOpen(false);
          return true;
        }
        if (!filteredVariables.length) return false;
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setActiveVariableIndex((prev) => (prev + 1) % filteredVariables.length);
          return true;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setActiveVariableIndex((prev) => (prev - 1 + filteredVariables.length) % filteredVariables.length);
          return true;
        }
        if (event.key === "Enter") {
          event.preventDefault();
          const entry = filteredVariables[activeVariableIndex];
          if (entry) handleInsertVariable(entry[0]);
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const isEditorReady = useMemo(() => Boolean(editor), [editor]);

  useEffect(() => {
    if (editorRef) {
      editorRef.current = editor ?? null;
      return () => {
        editorRef.current = null;
      };
    }
    return;
  }, [editor, editorRef]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value === current) return;
    editor.commands.setContent(value || "", false);
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    const syncFormatting = () => {
      const font = editor.getAttributes("textStyle").fontFamily;
      if (font && font !== selectedFont) {
        setSelectedFont(font);
      }
      const color = editor.getAttributes("textStyle").color;
      if (color && color !== selectedColor) {
        setSelectedColor(color);
      }
    };
    editor.on("selectionUpdate", syncFormatting);
    editor.on("transaction", syncFormatting);
    return () => {
      editor.off("selectionUpdate", syncFormatting);
      editor.off("transaction", syncFormatting);
    };
  }, [editor, selectedFont, selectedColor]);

  useEffect(() => {
    if (!editor || !enableVariableCommands || !variableEntries.length) {
      setVariableCommandOpen(false);
      return;
    }
    const handleTransaction = () => {
      const { $from } = editor.state.selection;
      const parentText = $from.parent.textBetween(0, $from.parentOffset, " ");
      const match = VARIABLE_COMMAND_REGEX.exec(parentText);
      if (!match) {
        setVariableCommandOpen(false);
        return;
      }
      const commandLength = match[0].length;
      const from = $from.pos - commandLength;
      const to = $from.pos;
      setVariableCommandRange({ from, to });
      setVariableCommandQuery(match[1] ?? "");
      setVariableCommandOpen(true);
    };
    editor.on("transaction", handleTransaction);
    return () => {
      editor.off("transaction", handleTransaction);
    };
  }, [editor, enableVariableCommands, variableEntries.length]);

  useEffect(() => {
    if (!variableCommandOpen) return;
    setActiveVariableIndex(0);
  }, [variableCommandOpen, variableCommandQuery]);

  useEffect(() => {
    if (!filteredVariables.length) {
      setActiveVariableIndex(0);
      return;
    }
    setActiveVariableIndex((prev) => Math.min(prev, filteredVariables.length - 1));
  }, [filteredVariables.length]);

  useEffect(() => {
    if (!enableSourceToggle) {
      setMode("visual");
    }
  }, [enableSourceToggle]);

  const handleCreateLink = () => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href ?? "";
    const url = window.prompt("Enter the destination URL", previousUrl);
    if (url === null) return;
    if (url.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    const normalizedUrl = /^https?:\/\//i.test(url) ? url.trim() : `https://${url.trim()}`;
    editor.chain().focus().extendMarkRange("link").setLink({ href: normalizedUrl, target: "_blank" }).run();
  };

  const handleFontChange = (nextFont: string) => {
    if (!editor) return;
    setSelectedFont(nextFont);
    editor.chain().focus().setFontFamily(nextFont === "inherit" ? "" : nextFont).run();
  };

  const handleColorChange = (nextColor: string) => {
    if (!editor) return;
    setSelectedColor(nextColor);
    editor.chain().focus().setColor(nextColor).run();
  };

  const isVisualMode = !enableSourceToggle || mode === "visual";
  const handleInsertVariable = (token: string) => {
    if (!editor || !variableCommandRange) return;
    editor
      .chain()
      .focus()
      .deleteRange(variableCommandRange)
      .insertContent({ type: "text", text: `{{ ${token} }}` })
      .run();
    setVariableCommandOpen(false);
    setVariableCommandQuery("");
    setVariableCommandRange(null);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {enableSourceToggle && (
        <div className="inline-flex rounded-full border border-border bg-background/30 p-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {(["visual", "html"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setMode(tab)}
              className={cn(
                "rounded-full px-3 py-1 transition-all duration-200",
                mode === tab ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tab === "visual" ? "Visual" : "HTML"}
            </button>
          ))}
        </div>
      )}
      {isVisualMode ? (
        <>
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background/40 px-2 py-1 text-xs text-foreground/80">
            <ToolbarButton
              label="Bold"
              active={editor?.isActive("bold")}
              disabled={!isEditorReady}
              onClick={() => editor?.chain().focus().toggleBold().run()}
            >
              B
            </ToolbarButton>
            <ToolbarButton
              label="Italic"
              active={editor?.isActive("italic")}
              disabled={!isEditorReady}
              onClick={() => editor?.chain().focus().toggleItalic().run()}
            >
              I
            </ToolbarButton>
            <ToolbarButton
              label="Underline"
              active={editor?.isActive("underline")}
              disabled={!isEditorReady}
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
            >
              U
            </ToolbarButton>
            <ToolbarButton
              label="Bulleted list"
              active={editor?.isActive("bulletList")}
              disabled={!isEditorReady}
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
            >
              •
            </ToolbarButton>
            <ToolbarButton
              label="Numbered list"
              active={editor?.isActive("orderedList")}
              disabled={!isEditorReady}
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            >
              1.
            </ToolbarButton>
            <ToolbarButton
              label="Quote"
              active={editor?.isActive("blockquote")}
              disabled={!isEditorReady}
              onClick={() => editor?.chain().focus().toggleBlockquote().run()}
            >
              ❝
            </ToolbarButton>
            <span className="mx-1 h-4 w-px bg-muted/80" aria-hidden="true" />
            <ToolbarButton label="Link" disabled={!isEditorReady} onClick={handleCreateLink}>
              Link
            </ToolbarButton>
            <ToolbarButton
              label="Remove link"
              disabled={!isEditorReady}
              onClick={() => editor?.chain().focus().unsetLink().run()}
            >
              Unlink
            </ToolbarButton>
            <label className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
              Font
              <select
                className="rounded border border-border bg-background/40 px-2 py-1 text-[11px] text-foreground"
                value={selectedFont}
                onChange={(event) => handleFontChange(event.target.value)}
                disabled={!isEditorReady}
              >
                {FONT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
              Color
              <input
                type="color"
                value={selectedColor}
                onChange={(event) => handleColorChange(event.target.value)}
                className="h-7 w-10 cursor-pointer rounded border border-border bg-transparent p-0"
                disabled={!isEditorReady}
              />
            </label>
          </div>
          <div className="relative">
            <EditorContent editor={editor} style={{ minHeight }} />
            {enableVariableCommands && variableCommandOpen && (
              <div className="absolute right-3 top-3 z-10 w-72 rounded-xl border border-border/60 bg-background/95 p-2 text-xs shadow-xl">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Insert variable</p>
                  <button
                    type="button"
                    className="text-[11px] text-muted-foreground hover:text-foreground"
                    onClick={() => setVariableCommandOpen(false)}
                  >
                    Esc
                  </button>
                </div>
                <input
                  type="search"
                  value={variableCommandQuery}
                  onChange={(event) => setVariableCommandQuery(event.target.value)}
                  placeholder="Type to filter variables…"
                  className="mt-2 w-full rounded-lg border border-border bg-background/50 px-2 py-1 text-[11px] text-foreground"
                  autoFocus
                />
                <div
                  className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-border/60 bg-background/40 p-1"
                  role="listbox"
                >
                  {filteredVariables.length === 0 ? (
                    <p className="p-2 text-[11px] text-muted-foreground">No matching variables.</p>
                  ) : (
                    filteredVariables.map(([key, description], index) => (
                      <button
                        key={key}
                        type="button"
                        className={cn(
                          "flex w-full flex-col items-start gap-1 rounded-lg px-2 py-1 text-left",
                          activeVariableIndex === index ? "bg-primary/10" : "hover:bg-primary/10",
                        )}
                        role="option"
                        aria-selected={activeVariableIndex === index}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleInsertVariable(key)}
                      >
                        <span className="font-mono text-[11px] text-success">{`{{ ${key} }}`}</span>
                        {description && <span className="text-[11px] text-muted-foreground">{description}</span>}
                      </button>
                    ))
                  )}
                </div>
                <p className="mt-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                  Tip: type <span className="font-mono">{'{{'}  </span>
                </p>
              </div>
            )}
          </div>
        </>
      ) : (
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          spellCheck={false}
          className="w-full rounded-lg border border-border bg-background/40 px-3 py-2 font-mono text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          style={{ minHeight }}
        />
      )}
    </div>
  );
}

type ToolbarButtonProps = {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
};

function ToolbarButton({ label, active, disabled, onClick, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "rounded px-2 py-1 text-xs font-semibold uppercase tracking-wide text-foreground/80 transition-all duration-200",
        active ? "bg-primary/30 text-primary" : "hover:bg-muted/70",
        disabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
      )}
      onClick={onClick}
      title={label}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
